/**
 * useShellOpposedAction - resolve an opposed drum action against the
 * current defender.
 *
 * Both sides roll at once (the user's ruling), the outcome lands as
 * conditions/wounds, and ONE combined entry reaches the log. All of it sits
 * behind one snapshot, so a single Ctrl+Z takes back the whole exchange.
 *
 * The resolution is a PLAN - `opposedActionPlan(def, ctx)` rolls the dice
 * and says what changes - and the hook applies it: the defender's write
 * first, then the actor's, then the log line, exactly the order the inline
 * version wrote in. Returns `executeOpposedAction(def)`.
 */
import { useCallback } from 'react';
import { actionBeat } from '../engine/attackBeat';
import { hasWS } from '../engine/weaponSpecials';
import { sizeStepsBetween } from '../engine/sizes';
import { resolveFeint, resolveKnockDown, resolveStun } from '../engine/actions/resolvers';
import { buildOpposedNarrative, rollOpposedTest, plainAxisFor, characteristicOf } from '../engine/opposedTests';
import { calcSkillTarget } from '../engine/characteristicTests';
import { COST_LABELS } from '../engine/actions/types';
import { reactionExposure } from '../engine/exposures';
import { NOTCH_AT_REST } from '../engine/actions/difficultyNotch';
import { dropHeldAim, mergeAll } from './shellCombatant';

/** A side carrying the notch's modifier for it; at rest the side is untouched. */
const notched = (side, mod) => (mod ? { ...side, mod: (side.mod ?? 0) + mod } : side);

/** Either the defender's own characteristic, or a plain target's test - at the notch's "other side". */
function defenderSide(ctx, charId, plainTestId) {
  const side = ctx.defChar
    ? { kind: 'characteristic', character: ctx.defChar, charId }
    : { kind: 'plain', target: ctx.target, testId: plainTestId };
  return notched(side, ctx.notch?.theirs);
}

/** The actor's own characteristic, as a side of a test, at the notch's difficulty. */
function actorSide(ctx, charId) {
  return notched({ kind: 'characteristic', character: ctx.actor.sheet, charId }, ctx.notch?.mine);
}

/**
 * A character's side for a catalog subject: the skill when they can roll
 * it, else the characteristic it rests on.
 */
function subjectSide(character, subject) {
  if (subject.kind === 'skill' && calcSkillTarget(character, subject.id).rollable) {
    return { kind: 'skill', character, skillId: subject.id };
  }
  return { kind: 'characteristic', character, charId: characteristicOf(subject) };
}

/** The defender's side for a catalog subject; a stat block answers on the plain axis under it. */
function defenderSubjectSide(ctx, subject) {
  if (ctx.defChar) return subjectSide(ctx.defChar, subject);
  return { kind: 'plain', target: ctx.target, testId: plainAxisFor(characteristicOf(subject)) };
}

/** Toughness Bonus with Unnatural Toughness folded in. */
function effectiveTB(target) {
  return (target.toughnessBonus || 0) * (target.unnaturalToughness || 1);
}

const signed = (n) => `${n > 0 ? '+' : ''}${n}`;

/**
 * An `escape` exposure (v8.368.6): the OTHER side of a grapple with the
 * bearer shifts - Clinging -20 to get free of them, Slippery +20.
 */
function escapeModOf(bearer, reader) {
  return reactionExposure(
    {
      conditions: bearer?.conditions ?? [],
      exposures: bearer?.exposures ?? [],
      teamTag: bearer?.teamTag ?? null,
      sideTag: bearer?.sideTag ?? null,
    },
    { teamTag: reader?.teamTag ?? null, sideTag: reader?.sideTag ?? null, isMelee: true },
  ).escapeMod;
}

/** A side with `mod` added to whatever it already carried. */
const withMod = (side, mod) => ({ ...side, mod: (side.mod ?? 0) + mod });

/** Grapple's exposures, applied to both sides, with a note for each. */
function gripSides(sides, ctx) {
  const vsTarget = escapeModOf(ctx.target, ctx.actorProjection);
  const vsActor = escapeModOf(ctx.actorProjection, ctx.target);
  const next = { ...sides };
  if (vsTarget) {
    next.actor = withMod(next.actor, vsTarget);
    next.notes.push(`${ctx.defenderName} is exposed: ${signed(vsTarget)} to ${ctx.actorName}'s side of the grapple.`);
  }
  if (vsActor) {
    next.defender = withMod(next.defender, vsActor);
    next.notes.push(`${ctx.actorName} is exposed: ${signed(vsActor)} to ${ctx.defenderName}'s side of the grapple.`);
  }
  return next;
}

/**
 * RAW Grapple: +1 Degree of Success per step of size difference, in all
 * Opposed Tests. It rides dosBonus, which counts toward the comparison and
 * never rewrites the roll.
 */
function sizeSides(sides, ctx) {
  const steps = sizeStepsBetween(ctx.actor.sheet?.size, ctx.target.size);
  if (steps === 0) return sides;
  const next = { ...sides };
  if (steps > 0) next.actor = { ...next.actor, dosBonus: steps };
  else next.defender = { ...next.defender, dosBonus: -steps };
  const bigger = steps > 0 ? ctx.actorName : ctx.defenderName;
  const step = Math.abs(steps);
  next.notes.push(`${bigger} is ${step} size step${step === 1 ? '' : 's'} larger: +${step} DoS.`);
  return next;
}

/** The catalog row's sentence for whoever won. */
function verdictLine(opposes, winner, ctx) {
  if (winner === 'actor')
    return opposes.onActorWin.replace('{winner}', ctx.actorName).replace('{loser}', ctx.defenderName);
  if (winner === 'defender')
    return opposes.onDefenderWin.replace('{winner}', ctx.defenderName).replace('{loser}', ctx.actorName);
  return opposes.onStandoff;
}

/**
 * A narrative opposed action (v8.305.0) is entirely a catalog row: both
 * sides roll what the row names for them, the log says who won and what that means, and
 * nothing else changes. The GM moves the tokens.
 */
function narrativePlan(def, ctx) {
  let sides = {
    actor: notched(subjectSide(ctx.actor.sheet, def.opposes.actor), ctx.notch?.mine),
    defender: notched(defenderSubjectSide(ctx, def.opposes.defender), ctx.notch?.theirs),
    notes: [],
  };
  if (def.id === 'grapple') sides = gripSides(sides, ctx);
  if (def.opposes.sizeBonus) sides = sizeSides(sides, ctx);
  const result = rollOpposedTest(sides.actor, sides.defender, { unnaturalDosMode: ctx.unnaturalDosMode });
  const line = verdictLine(def.opposes, result.winner, ctx);
  return {
    narrative: buildOpposedNarrative(result, ctx.actorName, ctx.defenderName, `${def.name} ${ctx.costNote}`, [
      ...sides.notes,
      line,
    ]),
  };
}

/** `notes` appended to a narrative's special notes, when there are any. */
function withNotes(narrative, notes) {
  if (notes.length) narrative.detail.specialNotes = [...(narrative.detail.specialNotes ?? []), ...notes];
  return narrative;
}

/** Feint: a win means the next melee attack cannot be Dodged or Parried. */
function feintPlan(ctx) {
  const out = resolveFeint(actorSide(ctx, 'ws'), defenderSide(ctx, 'ws', 'parry'), {
    unnaturalDosMode: ctx.unnaturalDosMode,
  });
  const narrative = buildOpposedNarrative(out.result, ctx.actorName, ctx.defenderName, `Feint ${ctx.costNote}`);
  if (!out.actorGains.length) return { narrative };
  return {
    narrative: withNotes(narrative, ['Feint won - the next melee attack cannot be Dodged or Parried.']),
    actorConds: out.actorGains,
  };
}

/** The log's account of a knock-down: who went Prone, and what the impact did. */
function knockDownNotes(out, ctx) {
  const notes = [];
  if (out.defenderConds.length) {
    notes.push(`${ctx.defenderName} is knocked Prone.`);
    if (out.damage) {
      notes.push(
        `Impact: rolled ${out.damage.rolled}, soaked ${out.damage.soaked} (AP doubled) - ${out.damage.dealt} damage, +1 Fatigue.`,
      );
    }
  }
  if (out.actorConds.length) notes.push(`${ctx.actorName} is knocked Prone instead!`);
  return notes;
}

/** Knock-Down: opposed Strength; the loser (either side) goes Prone. */
function knockDownPlan(ctx) {
  const { target } = ctx;
  const out = resolveKnockDown({
    actor: actorSide(ctx, 'str'),
    defender: defenderSide(ctx, 'str', 'strength'),
    actorSB: ctx.attackerCharStats?.strengthBonus ?? 3,
    defenderBodyAP: target.armour?.body ?? 0,
    defenderTB: effectiveTB(target),
    unnaturalDosMode: ctx.unnaturalDosMode,
  });
  const narrative = buildOpposedNarrative(out.result, ctx.actorName, ctx.defenderName, `Knock-Down ${ctx.costNote}`);
  return {
    narrative: withNotes(narrative, knockDownNotes(out, ctx)),
    defender: out.defenderConds.length ? { conds: out.defenderConds, dealt: out.damage?.dealt ?? 0 } : null,
    actorConds: out.actorConds.length ? out.actorConds : null,
  };
}

/** The log's account of a stun: the contest, and whether it took. */
function stunNotes(out, ctx, primitive) {
  const notes = [];
  if (out.contest) {
    notes.push(
      `Contest: ${ctx.actorName} ${out.contest.actorTotal} vs ${ctx.defenderName} ${out.contest.defenderTotal}${primitive ? ' (head AP doubled - Primitive)' : ''}.`,
    );
  }
  if (out.defenderConds.length) {
    notes.push(
      `${ctx.defenderName} is Stunned for ${out.contest.diff} round${out.contest.diff === 1 ? '' : 's'}, +1 Fatigue.`,
    );
  } else if (out.contest) {
    notes.push('The blow lands but the senses hold - no Stun.');
  }
  return notes;
}

/** The stun's narrative: one Hard WS test, not an opposed pair. */
function stunNarrative(out, ctx, notes) {
  const { test } = out;
  const testLine = `${test.roll} vs ${test.target} - ${test.success ? `Success (${test.dos} DoS)` : `Failure (${test.dof} DoF)`}`;
  return {
    summary: `Stun ${ctx.costNote}: ${ctx.actorName} WS (Hard -20) - ${testLine}`,
    detail: {
      rolls: [
        { label: `${ctx.actorName}: WS Test (Hard -20)`, roll: test.roll, target: test.target, success: test.success },
      ],
      modifiers: [
        { label: `Base WS`, value: test.baseValue },
        { label: 'Stun (Hard)', value: -20 },
      ],
      ...(notes.length ? { specialNotes: notes } : {}),
    },
    outcome: out.defenderConds.length ? 'hit' : 'miss',
  };
}

/** Stun: a Hard WS test, then Strength against Toughness and head armour. */
function stunPlan(ctx) {
  const { target } = ctx;
  const primitive = hasWS(ctx.slot?.specialRules || [], 'Primitive');
  const out = resolveStun({
    actor: actorSide(ctx, 'ws'),
    actorSB: ctx.attackerCharStats?.strengthBonus ?? 3,
    defenderTB: effectiveTB(target),
    defenderHeadAP: target.armour?.head ?? 0,
    apDoubled: primitive,
    unnaturalDosMode: ctx.unnaturalDosMode,
  });
  return {
    narrative: stunNarrative(out, ctx, stunNotes(out, ctx, primitive)),
    defender: out.defenderConds.length ? { conds: out.defenderConds, dealt: 0 } : null,
  };
}

/** The decisive actions that are not narrative, by id - a lookup, not a branch. */
const PLANS = { feint: feintPlan, knockDown: knockDownPlan, stun: stunPlan };

/**
 * Roll a drum action and say what it changes: `{ narrative, defender?,
 * actorConds? }`, or null for an id nothing resolves. Pure apart from the
 * dice, which it rolls in the order the rules name them.
 */
export function opposedActionPlan(def, ctx) {
  if (def.opposes) return narrativePlan(def, ctx);
  return PLANS[def.id]?.(ctx) ?? null;
}

/** Who is involved, as the plan's context. The notch defaults to rest. */
function planContext(def, { actor, currentTab, notch, ...rest }) {
  const target = currentTab.target;
  return {
    ...rest,
    notch: notch ?? NOTCH_AT_REST,
    actor,
    target,
    actorName: actor.name || 'Attacker',
    defenderName: target.name || currentTab.label || 'Target',
    defChar: currentTab.sourceCharacter || null,
    costNote: `(${COST_LABELS[def.cost]})`,
  };
}

/** The plan's writes: the defender's first, then the actor's. */
function applyPlan(plan, tabId, actorId, { updateTab, updateCombatantState, stackPolicy }) {
  const { defender, actorConds } = plan;
  if (defender) {
    updateTab(tabId, (t) => ({
      ...t,
      target: {
        ...t.target,
        conditions: mergeAll(t.target.conditions ?? [], defender.conds, stackPolicy),
        ...(defender.dealt > 0
          ? { wounds: { ...t.target.wounds, current: t.target.wounds.current - defender.dealt } }
          : {}),
      },
    }));
  }
  if (actorConds) {
    updateCombatantState(actorId, (st) => ({
      ...st,
      conditions: mergeAll(st.conditions ?? [], actorConds, stackPolicy),
    }));
  }
}

/** Resolve, write, log - and an opposed action is not an attack, so a held aim is lost. */
function executeOpposed(def, deps) {
  const { actor, currentTab } = deps;
  if (!actor?.sheet || !currentTab) return;
  deps.capturePreActionSnapshot();
  deps.pushHistory();
  const plan = opposedActionPlan(def, planContext(def, deps));
  if (!plan) return;
  applyPlan(plan, currentTab.id, actor.id, deps);
  const at = Date.now();
  deps.addNarrative(currentTab.id, {
    type: 'test-result',
    text: plan.narrative.summary,
    structured: plan.narrative,
    timestamp: at,
    // The body action's beat (plan design-gaps, K2): a row that names one (`beat`) - a Manoeuvre's is the ram that
    // shoves the loser (Z1).
    ...(def.beat
      ? {
          beat: actionBeat(
            actor.id,
            currentTab.id,
            { id: def.id, plays: def.beat.plays },
            plan.narrative.outcome === 'hit',
            at,
          ),
        }
      : {}),
  });
  dropHeldAim(actor, `${def.name} is not an attack.`, deps);
  // The notch was for this test: back to rest.
  deps.setNotch?.(NOTCH_AT_REST);
  deps.historyTrack.current = true;
}

/**
 * @param {object} deps - actor, currentTab, actorProjection, attackerCharStats,
 *   slot, stackPolicy, unnaturalDosMode, notch and setNotch (the drum's
 *   difficulty notch, spent by the action), and the writers: updateTab,
 *   updateCombatantState, addNarrative, addRollNarrative,
 *   capturePreActionSnapshot, pushHistory, historyTrack.
 *
 * The callback depends on everything it reads; it once omitted
 * `stackPolicy` and `actorProjection` and served their previous values until
 * something listed moved (v8.377.3).
 *
 * Over the length target on purpose: it is a dependency list and the
 * object it binds, both declarative.
 */
export default function useShellOpposedAction(deps) {
  const { actor, currentTab, attackerCharStats, slot, unnaturalDosMode, stackPolicy, actorProjection } = deps;
  const { capturePreActionSnapshot, pushHistory, updateCombatantState, updateTab } = deps;
  const { addNarrative, addRollNarrative, historyTrack, notch, setNotch } = deps;
  return useCallback(
    (def) =>
      executeOpposed(def, {
        actor,
        currentTab,
        attackerCharStats,
        slot,
        unnaturalDosMode,
        stackPolicy,
        actorProjection,
        capturePreActionSnapshot,
        pushHistory,
        updateCombatantState,
        updateTab,
        addNarrative,
        addRollNarrative,
        historyTrack,
        notch,
        setNotch,
      }),
    [
      notch,
      setNotch,
      stackPolicy,
      actorProjection,
      actor,
      currentTab,
      attackerCharStats,
      slot,
      capturePreActionSnapshot,
      pushHistory,
      updateCombatantState,
      updateTab,
      addNarrative,
      addRollNarrative,
      historyTrack,
      unnaturalDosMode,
    ],
  );
}
